import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { env } from 'next-runtime-env';

import { useEffect, useState } from 'react';

// Add flags to track initialization
let rumInitialized = false;
let logsInitialized = false;

// Store original console methods
const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
  debug: console.debug,
};

// Override console methods to send to DataDog Logs (preferred) or RUM (fallback)
const overrideConsoleForDataDog = () => {
  const globalLogToDataDog = env('NEXT_PUBLIC_LOG_TO_DATADOG') === 'true';

  // Individual log type controls, defaulting to global setting
  const logErrorToDataDog = env('NEXT_PUBLIC_LOG_ERROR_TO_DATADOG')
    ? env('NEXT_PUBLIC_LOG_ERROR_TO_DATADOG') === 'true'
    : globalLogToDataDog;
  const logWarnToDataDog = env('NEXT_PUBLIC_LOG_WARN_TO_DATADOG')
    ? env('NEXT_PUBLIC_LOG_WARN_TO_DATADOG') === 'true'
    : globalLogToDataDog;
  const logInfoToDataDog = env('NEXT_PUBLIC_LOG_INFO_TO_DATADOG')
    ? env('NEXT_PUBLIC_LOG_INFO_TO_DATADOG') === 'true'
    : globalLogToDataDog;
  const logDebugToDataDog = env('NEXT_PUBLIC_LOG_DEBUG_TO_DATADOG')
    ? env('NEXT_PUBLIC_LOG_DEBUG_TO_DATADOG') === 'true'
    : globalLogToDataDog;
  const logLogToDataDog = env('NEXT_PUBLIC_LOG_LOG_TO_DATADOG')
    ? env('NEXT_PUBLIC_LOG_LOG_TO_DATADOG') === 'true'
    : globalLogToDataDog;

  // Individual masking controls, defaulting to global setting
  const globalMaskConsole = env('NEXT_PUBLIC_MASK_CONSOLE_LOGS') === 'true';
  const maskErrorConsole = env('NEXT_PUBLIC_MASK_ERROR_CONSOLE_LOGS')
    ? env('NEXT_PUBLIC_MASK_ERROR_CONSOLE_LOGS') === 'true'
    : globalMaskConsole;
  const maskWarnConsole = env('NEXT_PUBLIC_MASK_WARN_CONSOLE_LOGS')
    ? env('NEXT_PUBLIC_MASK_WARN_CONSOLE_LOGS') === 'true'
    : globalMaskConsole;
  const maskInfoConsole = env('NEXT_PUBLIC_MASK_INFO_CONSOLE_LOGS')
    ? env('NEXT_PUBLIC_MASK_INFO_CONSOLE_LOGS') === 'true'
    : globalMaskConsole;
  const maskDebugConsole = env('NEXT_PUBLIC_MASK_DEBUG_CONSOLE_LOGS')
    ? env('NEXT_PUBLIC_MASK_DEBUG_CONSOLE_LOGS') === 'true'
    : globalMaskConsole;
  const maskLogConsole = env('NEXT_PUBLIC_MASK_LOG_CONSOLE_LOGS')
    ? env('NEXT_PUBLIC_MASK_LOG_CONSOLE_LOGS') === 'true'
    : globalMaskConsole;

  // Only proceed if at least one log type is enabled
  if (!logErrorToDataDog && !logWarnToDataDog && !logInfoToDataDog && !logDebugToDataDog && !logLogToDataDog) {
    return;
  }

  const isLogsInitialized = logsInitialized && typeof window?.DD_LOGS?.getInternalContext() !== 'undefined';

  console.log = (...args) => {
    if (!maskLogConsole) {
      originalConsole.log(...args);
    }
    if (logLogToDataDog) {
      // Prefer DataDog Logs, fallback to RUM
      if (isLogsInitialized) {
        datadogLogs.logger.info(args.join(' '));
      } else {
        datadogRum.addAction('console.log', { message: args.join(' ') });
      }
    }
  };

  console.error = (...args) => {
    if (!maskErrorConsole) {
      originalConsole.error(...args);
    }
    if (logErrorToDataDog) {
      // Prefer DataDog Logs, fallback to RUM
      if (isLogsInitialized) {
        datadogLogs.logger.error(args.join(' '));
      } else {
        datadogRum.addError(new Error(args.join(' ')), { source: 'console.error' });
      }
    }
  };

  console.warn = (...args) => {
    if (!maskWarnConsole) {
      originalConsole.warn(...args);
    }
    if (logWarnToDataDog) {
      // Prefer DataDog Logs, fallback to RUM
      if (isLogsInitialized) {
        datadogLogs.logger.warn(args.join(' '));
      } else {
        datadogRum.addAction('console.warn', { message: args.join(' '), level: 'warning' });
      }
    }
  };

  console.info = (...args) => {
    if (!maskInfoConsole) {
      originalConsole.info(...args);
    }
    if (logInfoToDataDog) {
      // Prefer DataDog Logs, fallback to RUM
      if (isLogsInitialized) {
        datadogLogs.logger.info(args.join(' '));
      } else {
        datadogRum.addAction('console.info', { message: args.join(' ') });
      }
    }
  };

  console.debug = (...args) => {
    if (!maskDebugConsole) {
      originalConsole.debug(...args);
    }
    if (logDebugToDataDog) {
      // Prefer DataDog Logs, fallback to RUM
      if (isLogsInitialized) {
        datadogLogs.logger.debug(args.join(' '));
      } else {
        datadogRum.addAction('console.debug', { message: args.join(' '), level: 'debug' });
      }
    }
  };

  // Report which log types are enabled
  const enabledTypes = [];
  if (logLogToDataDog) enabledTypes.push('log');
  if (logErrorToDataDog) enabledTypes.push('error');
  if (logWarnToDataDog) enabledTypes.push('warn');
  if (logInfoToDataDog) enabledTypes.push('info');
  if (logDebugToDataDog) enabledTypes.push('debug');

  // Report which log types are masked
  const maskedTypes = [];
  if (maskLogConsole) maskedTypes.push('log');
  if (maskErrorConsole) maskedTypes.push('error');
  if (maskWarnConsole) maskedTypes.push('warn');
  if (maskInfoConsole) maskedTypes.push('info');
  if (maskDebugConsole) maskedTypes.push('debug');

  if (maskedTypes.length > 0) {
    console.log(
      `Console logs masked from browser for: ${maskedTypes.join(', ')}, sent to DataDog for: ${enabledTypes.join(', ')}`,
    );
  } else if (isLogsInitialized) {
    console.log(`Console methods overridden to send to DataDog Logs for: ${enabledTypes.join(', ')}`);
  } else {
    console.log(
      `Console methods overridden to send to DataDog RUM for: ${enabledTypes.join(', ')} (Logs not available)`,
    );
  }
};

// Function to set user context in both DataDog RUM and Logs
export const setDataDogUserContext = (userId: string) => {
  // Set user context in RUM
  if (typeof window?.DD_RUM?.getInternalContext() !== 'undefined') {
    try {
      datadogRum.setUser({
        id: userId,
      });
      console.log('DataDog RUM user context set:', { userId });
    } catch (error) {
      console.error('Error setting DataDog RUM user context:', error);
    }
  }

  // Set user context in Logs
  if (typeof window?.DD_LOGS?.getInternalContext() !== 'undefined') {
    try {
      datadogLogs.setUser({
        id: userId,
      });
      console.log('DataDog Logs user context set:', { userId });
    } catch (error) {
      console.error('Error setting DataDog Logs user context:', error);
    }
  }
};

/** Same-origin `/api/*` requests (Next.js BFF); pairs with trace forwarding in `routeHandler.ts`. */
const isSameOriginApiProxyUrl = (requestUrl: string): boolean => {
  try {
    const parsed = new URL(requestUrl, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

const parseTraceSampleRate = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw === '') {
    return fallback;
  }
  const n = Number(raw);
  if (Number.isNaN(n)) {
    return fallback;
  }
  return Math.min(100, Math.max(0, n));
};

// Helper function to extract version from branch name
const getVersionFromBranch = (branch: string): string => {
  return branch.split('release/')[1] ?? branch.split('release/')[0] ?? branch;
};

// Fetch build information directly (without React Query)
const fetchBuildInfo = async (): Promise<string> => {
  try {
    const response = await fetch('/api/build', { cache: 'no-store' });
    if (response.ok) {
      const data = await response.json();
      const branch = data?.data?.client?.branch;
      if (branch) {
        return getVersionFromBranch(branch);
      }
    }
  } catch (error) {
    console.warn('Failed to fetch build info for DataDog version:', error);
  }
  return 'unknown';
};

export default function DatadogInit() {
  const [appVersion, setAppVersion] = useState<string>('unknown');

  // Fetch build information on mount
  useEffect(() => {
    fetchBuildInfo().then(version => {
      setAppVersion(version);
      console.log('DataDog will use app version:', version);
    });
  }, []);

  // Initialize DataDog RUM
  useEffect(() => {
    // Check if already initialized or if we don't have version yet
    if (rumInitialized || typeof window?.DD_RUM?.getInternalContext() !== 'undefined' || appVersion === 'unknown') {
      if (appVersion === 'unknown') return; // Wait for version
      console.log('DataDog RUM already initialized, skipping initialization');
      return;
    }

    const currentEnvironment =
      window && window?.location.host === 'access-dev.atlas-sp.com'
        ? 'dev'
        : window?.location.host === 'access-uat.atlas-sp.com'
          ? 'uat'
          : window?.location.host === 'access.atlas-sp.com'
            ? 'prod'
            : 'localhost';

    console.log('Initializing DataDog RUM with version:', appVersion);

    // Use reduced monitoring in development to improve performance
    const isDevelopment = currentEnvironment === 'localhost';

    const traceSampleRate = parseTraceSampleRate(
      env('NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE'),
      isDevelopment ? 20 : 100,
    );

    try {
      datadogRum.init({
        applicationId: env('NEXT_PUBLIC_DATADOG_APP_ID') || '59db0b70-58a8-4ca2-b0ad-780e45aec442',
        clientToken: env('NEXT_PUBLIC_DATADOG_CLIENT_TOKEN') || 'pub3a5ed9e5ee9249ae939f40681b80e7ae',
        site: 'datadoghq.com',
        service: 'aga-portal-data-dog-application',
        env: currentEnvironment,
        version: appVersion,
        sessionSampleRate: isDevelopment ? 10 : 100, // Only 10% in dev
        sessionReplaySampleRate: isDevelopment ? 0 : 100, // No replay in dev
        trackUserInteractions: !isDevelopment, // Disable in dev
        trackResources: !isDevelopment, // Disable in dev
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        // Link RUM to backend APM: inject W3C headers on fetches to the Next.js `/api/*` proxy.
        allowedTracingUrls: [{ match: isSameOriginApiProxyUrl, propagatorTypes: ['tracecontext'] }],
        traceSampleRate,
      });
      rumInitialized = true;
      console.log('✅ DataDog RUM initialized successfully with version:', appVersion);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('datadog-rum-ready'));
      }
    } catch (error) {
      console.error('❌ Error initializing DataDog RUM:', error);
    }
  }, [appVersion]);

  // Initialize DataDog Logs
  useEffect(() => {
    if (appVersion === 'unknown') return; // Wait for version

    if (!env('NEXT_PUBLIC_LOG_TO_DATADOG')) {
      console.log('DataDog Logs initialization skipped - NEXT_PUBLIC_LOG_TO_DATADOG not enabled');
      return;
    }

    if (env('NEXT_PUBLIC_DATADOG_LOGS_ENABLED') === 'false') {
      console.log('DataDog Logs initialization skipped - NEXT_PUBLIC_DATADOG_LOGS_ENABLED disabled');
      return;
    }

    if (logsInitialized) {
      console.log('DataDog Logs already initialized, skipping initialization');
      return;
    }

    const currentEnvironment =
      window && window?.location.host === 'access-dev.atlas-sp.com'
        ? 'dev'
        : window?.location.host === 'access-uat.atlas-sp.com'
          ? 'uat'
          : window?.location.host === 'access.atlas-sp.com'
            ? 'prod'
            : 'localhost';

    console.log('Initializing DataDog Logs with version:', appVersion);

    try {
      datadogLogs.init({
        clientToken: env('NEXT_PUBLIC_DATADOG_CLIENT_TOKEN') || 'pub3a5ed9e5ee9249ae939f40681b80e7ae',
        site: 'datadoghq.com',
        service: 'aga-portal-data-dog-application',
        env: currentEnvironment,
        version: appVersion,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });

      logsInitialized = true;
      console.log('✅ DataDog Logs initialized successfully with version:', appVersion);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('datadog-logs-ready'));
      }
    } catch (error) {
      console.error('❌ Error initializing DataDog Logs:', error);
    }
  }, [appVersion]);

  // Apply console override after both RUM and Logs are initialized (or skipped)
  useEffect(() => {
    if (appVersion === 'unknown') return; // Wait for version

    // Apply override after initialization attempts
    const timeoutId = setTimeout(() => {
      overrideConsoleForDataDog();
    }, 100); // Small delay to ensure initialization is complete

    return () => clearTimeout(timeoutId);
  }, [appVersion]);

  // Start session replay
  useEffect(() => {
    if (typeof window?.DD_RUM?.getInternalContext() !== 'undefined') {
      try {
        datadogRum.startSessionReplayRecording();
        console.log('Datadog session replay recording started');
      } catch (error) {
        console.error('Error starting Datadog session replay:', error);
      }
    }
  }, []);

  return null;
}
